import React, { useState, useEffect } from "react";
import {
  Eye,
  Plus,
  Edit2,
  Trash2,
  GitCompare,
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  Globe,
  Shield,
  Zap,
  X,
} from "lucide-react";
import { theme } from "../../../styles/theme";
import lensesApi, {
  StakeholderLens,
  LensWeights,
} from "../../../api/lensesApi";

// Import sibling components
const LensComparison = React.lazy(() => import('./LensComparison'));
const LensWeightEditor = React.lazy(() => import('./LensWeightEditor'));

interface LensManagementProps {
  darkMode: boolean;
}

const LensManagement: React.FC<LensManagementProps> = ({ darkMode }) => {
  const [lenses, setLenses] = useState<StakeholderLens[]>([]);
  const [selectedLens, setSelectedLens] = useState<StakeholderLens | null>(null);
  const [, setLoading] = useState(false);
  const [view, setView] = useState<"list" | "create" | "edit" | "compare">("list");
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Form states for creating/editing
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    weights: LensWeights;
  }>({
    name: "",
    description: "",
    weights: {
      wgtCost: 0.143,
      wgtLabour: 0.143,
      wgtRemit: 0.143,
      wgtSROI: 0.143,
      wgtSystemic: 0.143,
      wgtClimate: 0.143,
      wgtRisk: 0.142,
    },
  });

  useEffect(() => {
    fetchLenses();
  }, []);

  const fetchLenses = async () => {
    setLoading(true);
    try {
      const lenses = await lensesApi.getLenses({ active_only: false });
      setLenses(lenses);
    } catch (error) {
      console.error("Failed to fetch lenses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLens = async () => {
    try {
      const validation = lensesApi.validateWeights(formData.weights);
      if (!validation.is_valid) {
        alert(validation.errors.join("\n"));
        return;
      }

      await lensesApi.createLens(formData);
      await fetchLenses();
      setView("list");
      resetForm();
    } catch (error) {
      console.error("Failed to create lens:", error);
      alert("Failed to create lens");
    }
  };

  const handleUpdateLens = async () => {
    if (!selectedLens) return;

    try {
      // Update basic info
      await lensesApi.updateLens(selectedLens.id, {
        name: formData.name,
        description: formData.description,
      });

      // Update weights
      await lensesApi.updateLensWeights(selectedLens.id, formData.weights);

      await fetchLenses();
      setView("list");
      setSelectedLens(null);
      resetForm();
    } catch (error) {
      console.error("Failed to update lens:", error);
      alert("Failed to update lens");
    }
  };

  const handleDeleteLens = async (id: number) => {
    if (!window.confirm("Are you sure you want to deactivate this lens?")) {
      return;
    }

    try {
      await lensesApi.deleteLens(id);
      await fetchLenses();
    } catch (error) {
      console.error("Failed to delete lens:", error);
      alert("Failed to delete lens");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      weights: {
        wgtCost: 0.143,
        wgtLabour: 0.143,
        wgtRemit: 0.143,
        wgtSROI: 0.143,
        wgtSystemic: 0.143,
        wgtClimate: 0.143,
        wgtRisk: 0.142,
      },
    });
  };

  const handleEditLens = async (lens: StakeholderLens) => {
    try {
      setLoading(true);
      // Fetch the complete lens data with weights
      const fullLensData = await lensesApi.getLensById(lens.id);
      
      setSelectedLens(fullLensData);
      setFormData({
        name: fullLensData.name,
        description: fullLensData.description,
        weights: fullLensData.weights || {
          wgtCost: 0.143,
          wgtLabour: 0.143,
          wgtRemit: 0.143,
          wgtSROI: 0.143,
          wgtSystemic: 0.143,
          wgtClimate: 0.143,
          wgtRisk: 0.142,
        },
      });
      setView("edit");
    } catch (error) {
      console.error("Failed to load lens for editing:", error);
      alert("Failed to load lens details");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectForCompare = (lensId: number) => {
    if (selectedForCompare.includes(lensId)) {
      setSelectedForCompare(selectedForCompare.filter((id) => id !== lensId));
    } else if (selectedForCompare.length < 2) {
      setSelectedForCompare([...selectedForCompare, lensId]);
    }
  };

  const getClusterIcon = (key: string) => {
    const icons: { [key: string]: React.FC<any> } = {
      wgtCost: DollarSign,
      wgtLabour: Users,
      wgtRemit: TrendingUp,
      wgtSROI: BarChart3,
      wgtSystemic: Zap,
      wgtClimate: Globe,
      wgtRisk: Shield,
    };
    return icons[key] || Eye;
  };

  const filteredLenses = lenses.filter((lens) =>
    lens.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lens.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (view === "compare" && selectedForCompare.length === 2) {
    return (
      <React.Suspense fallback={<div>Loading...</div>}>
        <LensComparison
          lens1Id={selectedForCompare[0]}
          lens2Id={selectedForCompare[1]}
          darkMode={darkMode}
          onBack={() => {
            setView("list");
            setSelectedForCompare([]);
            setCompareMode(false);
          }}
        />
      </React.Suspense>
    );
  }

  if (view === "create" || view === "edit") {
    return (
      <React.Suspense fallback={<div>Loading...</div>}>
        <LensWeightEditor
          formData={formData}
          setFormData={setFormData}
          darkMode={darkMode}
          onSave={view === "create" ? handleCreateLens : handleUpdateLens}
          onCancel={() => {
            setView("list");
            setSelectedLens(null);
            resetForm();
          }}
          isEdit={view === "edit"}
        />
      </React.Suspense>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2
            className="text-lg font-semibold"
            style={{
              color: darkMode
                ? theme.colors.text.darkPrimary
                : theme.colors.text.primary,
            }}
          >
            Stakeholder Lenses
          </h2>
          <p
            className="text-sm mt-1"
            style={{ color: theme.colors.text.secondary }}
          >
            Manage and apply different stakeholder perspectives to impact analysis
          </p>
        </div>
        <div className="flex space-x-2">
          {compareMode && selectedForCompare.length === 2 && (
            <button
              onClick={() => setView("compare")}
              className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
              style={{
                backgroundColor: theme.colors.secondary.main,
                color: "white",
              }}
            >
              <GitCompare className="w-4 h-4" />
              <span>Compare Selected</span>
            </button>
          )}
          <button
            onClick={() => {
              setCompareMode(!compareMode);
              setSelectedForCompare([]);
            }}
            className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
            style={{
              backgroundColor: compareMode
                ? theme.colors.status.warning + "20"
                : darkMode
                ? theme.colors.background.dark
                : theme.colors.background.default,
              color: compareMode
                ? theme.colors.status.warning
                : darkMode
                ? theme.colors.text.darkSecondary
                : theme.colors.text.primary,
              border: `1px solid ${
                compareMode
                  ? theme.colors.status.warning
                  : darkMode
                  ? theme.colors.border.dark
                  : theme.colors.border.light
              }`,
            }}
          >
            <GitCompare className="w-4 h-4" />
            <span>{compareMode ? "Cancel Compare" : "Compare"}</span>
          </button>
          <button
            onClick={() => {
              resetForm();
              setView("create");
            }}
            className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
            style={{
              backgroundColor: theme.colors.primary.main,
              color: "white",
            }}
          >
            <Plus className="w-4 h-4" />
            <span>New Lens</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search lenses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 rounded-lg"
          style={{
            backgroundColor: darkMode
              ? theme.colors.background.dark
              : theme.colors.background.default,
            border: `1px solid ${
              darkMode ? theme.colors.border.dark : theme.colors.border.light
            }`,
            color: darkMode
              ? theme.colors.text.darkPrimary
              : theme.colors.text.primary,
          }}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            <X
              className="w-4 h-4"
              style={{ color: theme.colors.text.secondary }}
            />
          </button>
        )}
      </div>

      {/* Lenses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredLenses.map((lens) => (
          <div
            key={lens.id}
            className={`p-5 rounded-xl transition-all ${
              compareMode && selectedForCompare.includes(lens.id)
                ? "ring-2"
                : ""
            }`}
            style={{
              backgroundColor: darkMode
                ? theme.colors.background.dark
                : theme.colors.background.default,
              border: `1px solid ${
                compareMode && selectedForCompare.includes(lens.id)
                  ? theme.colors.primary.main
                  : darkMode
                  ? theme.colors.border.dark
                  : theme.colors.border.light
              }`,
            }}
          >
            {/* Lens Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Eye
                  className="w-5 h-5"
                  style={{ color: theme.colors.primary.main }}
                />
                <h3
                  className="font-semibold"
                  style={{
                    color: darkMode
                      ? theme.colors.text.darkPrimary
                      : theme.colors.text.primary,
                  }}
                >
                  {lens.name}
                </h3>
              </div>
              {compareMode ? (
                <input
                  type="checkbox"
                  checked={selectedForCompare.includes(lens.id)}
                  onChange={() => handleSelectForCompare(lens.id)}
                  className="w-4 h-4"
                  disabled={
                    !selectedForCompare.includes(lens.id) &&
                    selectedForCompare.length >= 2
                  }
                />
              ) : (
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    lens.is_active
                      ? "bg-green-500 bg-opacity-20 text-green-600"
                      : "bg-gray-500 bg-opacity-20 text-gray-600"
                  }`}
                >
                  {lens.is_active ? "Active" : "Inactive"}
                </span>
              )}
            </div>

            {/* Description */}
            <p
              className="text-sm mb-4 line-clamp-2"
              style={{ color: theme.colors.text.secondary }}
            >
              {lens.description}
            </p>

            {/* Weight Summary */}
            {lens.weights && (
              <div className="space-y-1 mb-4">
                <p
                  className="text-xs font-medium mb-2"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Weight Distribution
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(lens.weights).map(([key, value]) => {
                    const Icon = getClusterIcon(key);
                    const percentage = (value * 100).toFixed(0);
                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center space-x-1">
                          <Icon
                            className="w-3 h-3"
                            style={{ color: theme.colors.text.secondary }}
                          />
                          <span style={{ color: theme.colors.text.secondary }}>
                            {key.replace("wgt", "")}
                          </span>
                        </div>
                        <span
                          className="font-medium"
                          style={{
                            color:
                              value === 0
                                ? theme.colors.text.secondary
                                : value > 0.2
                                ? theme.colors.primary.main
                                : darkMode
                                ? theme.colors.text.darkPrimary
                                : theme.colors.text.primary,
                          }}
                        >
                          {percentage}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            {!compareMode && (
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditLens(lens)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-center space-x-1"
                  style={{
                    backgroundColor: theme.colors.primary.main + "20",
                    color: theme.colors.primary.main,
                  }}
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDeleteLens(lens.id)}
                  className="px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-center space-x-1"
                  style={{
                    backgroundColor: theme.colors.status.error + "20",
                    color: theme.colors.status.error,
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredLenses.length === 0 && (
        <div
          className="text-center py-12"
          style={{ color: theme.colors.text.secondary }}
        >
          <Eye className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No lenses found</p>
          <p className="text-sm mt-2">
            {searchTerm
              ? "Try adjusting your search criteria"
              : "Create your first lens to get started"}
          </p>
        </div>
      )}
    </div>
  );
};

export default LensManagement;